import {Timestamp} from '@firebase/firestore';
import {isFutureTimestamp} from './date-utils';
import {planNames, teamPlanNames} from '../common/text';
import dayjs from "dayjs";
import {SupportedLanguage} from './supported-language.model';
import {AffiliateSettings} from './affiliate-settings.model';
import {AffiliateProgramCookie} from './affiliate-program-cookie.model';
import { CommunityMemberSettings } from './community/community-member-settings.model';


export interface User {
  id: string;
  email?: string;
  invitationStatus?: "active" | "invited";
  unreadNotificationsCounter?:number;
  pictureUrl?: string;
  displayName?: string;
  pricingPlan?: string;
  isTeamPlan?: boolean;
  maxTeamSize?: number;
  planActivatedAt?: Timestamp;
  planEndsAt?: Timestamp;
  userCreatedAt?: Timestamp;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  cardLast4Digits?: string;
  cardExpirationMonth?: string;
  cardExpirationYear?: string;
  testUser?: boolean;
  addedToNewsletter?: boolean;
  purchasedCourses?: string[];
  temporaryAccess?: any;
  leadReportedToFacebook?: boolean;
  unsubscribeFromNewsletter?: boolean;
  newsletterBlockedReason?: 'bounce' | 'complaint';
  preferredLanguage?: SupportedLanguage;
  groupIds?: string[];
  groupNames?: string[];
  affiliateStatus?: 'active' | 'inactive';
  affiliateId?:string; // this is the unique affiliate ID of the user, in case they joined the creator affiliate program
  affiliateProgramCookie?: AffiliateProgramCookie;
  affiliateAmountDue?: number;
  affiliateAmountPaid?: number;
  affiliateSettings?: AffiliateSettings;
  isCommunityMember?: boolean;
  isActiveCommunityMember?: boolean;
  communityMemberSettings?: CommunityMemberSettings;
  lastSeenAt?: Timestamp;
  mixPanelInitialized?: boolean;
  invitationLinkId?: string;
  studentEnrollment?: StudentEnrollment;
  firstPaymentMade?: boolean;
  maxAssessmentAttempts?: MaxAssessmentAttempts;
  progressMatrix?: ProgressMatrix;
}

export interface ProgressMatrix {
  [courseId: string]: number;
}
export interface MaxAssessmentAttempts {
  [assessmentId: string]: number;
}

export interface StudentEnrollment {
  [x: string]: string;
}

export const ANONYMOUS_USER: User = {
  id: 'anonymous',
  testUser: false
};

export function isAnonymousUser(user: User) {
  return user && user.id == 'anonymous';
}

export function isUserPlanStillValid(user: User) {
  return user.pricingPlan && (!user.planEndsAt || isFutureTimestamp(user.planEndsAt));
}

export function isUserPlanCanceled(user: User) {
  return user.pricingPlan && user.planEndsAt;
}

export function subscriptionDescription(user: User) {
  if (!user.pricingPlan) {
    return 'None';
  }
  if (!user.isTeamPlan) {
    return planNames[user.pricingPlan];
  }
  if (user.isTeamPlan) {
    return teamPlanNames[user.pricingPlan];
  }
}

export function isSubscriptionActive(user: User) {
  return user && user.pricingPlan && (!user.planEndsAt || dayjs(user.planEndsAt.toMillis()).isAfter(dayjs()));
}

export function isSubscriptionCancelled(user: User) {
  return user && user.planEndsAt;
}

/**
 * Whether this user has a subscription on record at all - still running, or cancelled and
 * running out its paid period.
 *
 * Exists to gate whether someone is SHOWN their subscription, which is a different
 * question from whether the school is currently SELLING subscriptions. Those two were
 * conflated: "My Account" and the student profile hid the whole subscription card - the
 * Cancel button with it - whenever a school had switched its subscription screen off. A
 * school that stops selling still has existing subscribers, and those people were left
 * paying with no way to cancel from inside the product.
 */
export function hasSubscriptionPlan(user: User) {
  return !!user?.pricingPlan;
}

export function isLifetimeSubscriber(user: User) {
  return user.pricingPlan == "lifetime";
}

export function planActivationDate(user) {
  return new Date(user.planActivatedAt.toMillis());
}

export function planValidUntil(user: User) {
  return new Date(user?.planEndsAt?.toMillis() ?? new Date());
}

export function planEndsAtInDaysFromNow(user: User) {
  if (user && user.planEndsAt) {
    const now = dayjs();
    const planEndsAt = dayjs(user.planEndsAt.toMillis());
    const diff = planEndsAt.diff(now, 'day');
    return diff <= 7 ? diff : 0;
  } else {
    return 0;
  }
}
